import React, { createContext, useContext, useEffect, useState } from 'react';
import { DATA_ENDPOINT, LOCAL_FALLBACK } from '../config';

const PortalDataContext = createContext({ data: null, loading: true, source: null, error: null });

// ---------- Schema adapter -----------------------------------------------
// The portal renderer expects: { sections: [{ id, title, subtitle, description,
//   accent_color, icon, route, number, label, header_image, stats, cta_primary,
//   cta_secondary, entries: [ ... ] }] }
//
// New editorial schema uses: { categories: [{ id, title, description, image }],
//   items: [{ id?, category, title, date, summary, tags, link | pdf_link/ocr_link,
//   image }] }
//
// This adapter maps the new format to the internal one without changing any
// component code. If input already has `sections`, it is returned unchanged.

const CATEGORY_MAP = {
  ovnis: {
    internalId: 'ovnis',
    accent_color: '#a78bfa',
    icon: 'ufo',
    route: '/archives',
    number: 'I',
    label: 'ENTITÉ I',
    subtitle: 'Phénomènes Aériens Non Identifiés',
    stats: [
      { label: 'Dossiers UAP', value: '494' },
      { label: 'Vidéos officielles', value: '19' },
      { label: 'Tranches PURSUE', value: '4' },
    ],
    cta_primary: { to: '/archives', label: "Explorer l'archive UAP" },
    cta_secondary: { to: '/videos', label: 'Vidéos officielles' },
  },
  cia_fbi: {
    internalId: 'cia',
    accent_color: '#f87171',
    icon: 'classified',
    route: '/cia',
    number: 'II',
    label: 'ENTITÉ II',
    subtitle: 'Documents Classifiés Rendus Publics',
    stats: [
      { label: 'Documents CIA', value: '22' },
      { label: 'Programmes psi', value: '7' },
      { label: 'Rapports FBI', value: '131' },
    ],
    cta_primary: { to: '/cia', label: 'Consulter les dossiers CIA' },
    cta_secondary: { to: '/divulgations', label: 'Divulgations récentes' },
  },
  ordres_executifs: {
    internalId: 'ordres-executifs',
    accent_color: '#fbbf24',
    icon: 'executive',
    route: '/ordres-executifs',
    number: 'III',
    label: 'ENTITÉ III',
    subtitle: 'Décrets Présidentiels · Armes du Combat',
    stats: [
      { label: 'Décrets archivés', value: '7' },
      { label: 'Priorité stratégique', value: '#1 · #2' },
      { label: 'Traduction FR', value: '100%' },
    ],
    cta_primary: { to: '/ordres-executifs', label: 'Ouvrir les ordres exécutifs' },
    cta_secondary: { to: '/chronologie', label: 'Chronologie complète' },
  },
  covid: {
    internalId: 'covid',
    accent_color: '#dc2626',
    icon: 'virus',
    route: '/covid',
    number: 'IV',
    label: 'ENTITÉ IV',
    subtitle: 'La Vérité Confirmée · Origine · Dissimulation · Justice',
    stats: [
      { label: 'Dossiers', value: '5' },
      { label: 'Documents PDF', value: '2' },
      { label: 'Source', value: 'US Senate / House' },
    ],
    cta_primary: { to: '/covid', label: 'Ouvrir les dossiers COVID' },
    cta_secondary: { to: '/divulgations', label: 'Divulgations récentes' },
  },
};

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function mapItemToEntry(item, category) {
  const hasPdf = Boolean(item.pdf_link);
  const code = item.id && /^[A-Z]+-\d+/.test(item.id) ? item.id : null;
  const badge = hasPdf ? 'DÉCLASSIFIÉ' : (item.tags && item.tags[0]) || null;
  const entry = {
    id: item.id || slugify(item.title) || String(Math.random()).slice(2),
    title: item.title,
    summary: item.summary,
    date: item.date,
    tags: item.tags || [],
    image: item.image || '',
    badge,
  };

  if (hasPdf) {
    // Classified PDF document
    entry.code = code || item.id;
    entry.pdf_url = item.pdf_link;
    entry.ocr_link = item.ocr_link || null;
    entry.icon = category === 'covid' ? 'flame' : 'file';
    // Try to extract page count from summary text like "557 pages" or "1 141 pages"
    const pagesMatch = String(item.summary || '').match(/([0-9][0-9\s]*)\s*pages/i);
    if (pagesMatch) {
      const n = parseInt(pagesMatch[1].replace(/\s/g, ''), 10);
      if (!Number.isNaN(n)) entry.pages = n;
    }
    entry.source_authority = null;
  } else {
    // Editorial dossier (external link)
    entry.link = item.link || item.ocr_link || '';
    entry.tag = (item.tags && item.tags.length > 0) ? item.tags.join(' · ') : null;
    entry.icon = category === 'covid' ? 'flame' : 'file';
  }
  return entry;
}

export function adaptPortalData(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  // Already in internal shape — return as-is.
  if (Array.isArray(raw.sections)) return raw;

  const categories = Array.isArray(raw.categories) ? raw.categories : [];
  const items = Array.isArray(raw.items) ? raw.items : [];

  const sections = categories.map((c) => {
    const meta = CATEGORY_MAP[c.id] || {
      internalId: c.id,
      accent_color: '#fbbf24',
      icon: 'executive',
      route: `/${c.id}`,
      number: '?',
      label: 'ENTITÉ',
      subtitle: '',
      stats: [],
      cta_primary: { to: `/${c.id}`, label: 'Ouvrir' },
      cta_secondary: null,
    };
    const catItems = items.filter((it) => it.category === c.id);
    const entries = catItems.map((it) => mapItemToEntry(it, c.id));
    return {
      id: meta.internalId,
      title: c.title,
      subtitle: meta.subtitle,
      description: c.description || '',
      accent_color: meta.accent_color,
      icon: meta.icon,
      route: meta.route,
      number: meta.number,
      label: meta.label,
      header_image: c.image || '',
      stats: meta.stats,
      cta_primary: meta.cta_primary,
      cta_secondary: meta.cta_secondary,
      entries,
    };
  });

  return {
    meta: {
      last_updated: raw.site_info?.updated_at || '',
      portal_title: raw.site_info?.title || 'ARCHIVES SOUVERAINES',
      portal_motto: raw.site_info?.motto || '',
      hero_image: '',
    },
    sections,
  };
}
// -------------------------------------------------------------------------

async function fetchWithTimeout(url, options = {}, ms = 6000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

async function loadPortalData() {
  // 1. Try remote endpoint (single source of truth). No aggressive cache.
  //    Skip entirely when DATA_ENDPOINT is empty (bypass mode).
  if (DATA_ENDPOINT) {
    try {
      const res = await fetchWithTimeout(
        DATA_ENDPOINT,
        {
          mode: 'cors',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        },
        6000
      );
      if (res.ok) {
        const json = await res.json();
        const adapted = adaptPortalData(json);
        if (adapted && Array.isArray(adapted.sections)) {
          return { data: adapted, source: 'remote' };
        }
      }
    } catch (_e) {
      // fall through to local
    }
  }

  // 2. Fallback to local static JSON
  const local = await fetch(LOCAL_FALLBACK, { cache: 'no-store' });
  if (!local.ok) {
    throw new Error(`Local fallback failed: ${local.status}`);
  }
  const localJson = await local.json();
  return { data: adaptPortalData(localJson), source: 'local' };
}

export function PortalDataProvider({ children }) {
  const [state, setState] = useState({ data: null, loading: true, source: null, error: null });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data, source } = await loadPortalData();
        if (cancel) return;
        setState({ data, loading: false, source, error: null });
      } catch (e) {
        if (cancel) return;
        setState({ data: null, loading: false, source: null, error: e.message || 'Unknown error' });
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <PortalDataContext.Provider value={state}>
      {children}
    </PortalDataContext.Provider>
  );
}

export function usePortalData() {
  return useContext(PortalDataContext);
}

export function useSection(sectionId) {
  const { data, loading, error, source } = usePortalData();
  const section = data?.sections?.find(s => s.id === sectionId) || null;
  return { section, loading, error, source };
}
